import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import { observer } from 'mobx-react';
import React from 'react';
import { Route, RouteComponentProps, Switch } from 'react-router-dom';
import { deleteGroup } from '../AppShell/Authentication/AuthorisationAsync';
import { GroupIGroup } from '../AppShell/Authentication/AuthorisationModels';
import { formErrorMessage } from '../Authorisation.en';
import { AlertBanner, AlertBannerTypes } from '../Components/AlertBanner';
import { AsyncFetch } from '../Components/AsyncFetch';
import { ButtonMode, ButtonVariant } from '../Components/Button';
import { StyledDialog } from '../Components/Dialog/StyledDialog';
import { StyledDialogAppBar } from '../Components/Dialog/StyledDialogAppBar';
import { StyledDialogButton } from '../Components/Dialog/StyledDialogButton';
import { CloseIcon, DeleteIcon, PatientInformationIcon, PatientPermissionsIcon } from '../Components/Icons';
import { NavMenu, NavMenuItem } from '../Components/Layout/NavMenu';
import { LoadingPlaceholder } from '../Components/Placeholder';
import { StyledPrompt } from '../Components/Prompts/StyledPrompt';
import { displayMessage } from '../Config/ApplicationConfig';
import {
    getGroupPermissionsRoute,
    getGroupRoute,
    GroupRouteParams,
    routeForGroup,
    routeForGroupPermissions,
    routeForGroups,
    routeForNewGroup
} from '../Routes';
import { white50, divider } from '../Styles/Colours';
import { GroupFileInformation } from './GroupFileInformation';
import { GroupFileStore, SaveGroupInfoResults } from './GroupFileStore';
import { GroupPermissions } from './Permissions/GroupFilePermissions';

type GroupFileProps = RouteComponentProps<GroupRouteParams> & { onDataChanged(): void };

@observer
export class GroupFile extends React.Component<GroupFileProps> {
    public state = {
        isDeleting: false,
        confirmDeleteDialog: false
    };

    public groupFileStore: GroupFileStore = new GroupFileStore();

    public close = () => {
        this.props.history.push(routeForGroups);
    };

    public componentDidMount() {
        if (this.props.match.params.id !== 'new' && this.props.match.params.id) {
            this.groupFileStore.loadAvatarGroupInformation(this.props.match.params.id);
        }
    }

    public componentWillUnmount() {
        this.props.onDataChanged();
    }

    public saveGroupInformation = async (group: GroupIGroup): Promise<SaveGroupInfoResults> => {
        const newUser = this.props.match.params.id === 'new';
        const groupId = newUser ? undefined : this.props.match.params.id;

        const saveGroupInfoResults = await this.groupFileStore.saveGroupInformation(groupId)(group);

        if (this.props.match.params.id !== 'new' && this.props.match.params.id) {
            this.groupFileStore.loadAvatarGroupInformation(this.props.match.params.id);
        }

        return saveGroupInfoResults;
    };

    public hideDeleteDialog = () => {
        this.setState({ confirmDeleteDialog: false });
    };

    public onDelete = () => {
        this.setState({ confirmDeleteDialog: true });
    };

    public deleteGroup = async () => {
        this.setState({ isDeleting: true }, async () => {
            await deleteGroup(this.props.match.params.id);
            displayMessage('Group deleted');
            this.props.onDataChanged();
        });

        this.setState({ confirmDeleteDialog: false }, () => {
            this.close();
        });
    };

    public sideBarRenderFunction = ({ collapsed }: { collapsed: boolean }) => {
        const newGroup = this.props.match.params.id.toLowerCase() === 'new';

        return (
            <>
                <NavMenu>
                    <NavMenuItem
                        icon={<PatientInformationIcon size='24' noCircularContainer />}
                        exact
                        title={'Account information'}
                        pathname={this.props.location.pathname}
                        location={getGroupRoute(this.props.match.params.id)}
                        referrerLocation={this.props.location}
                    />
                    <NavMenuItem
                        icon={
                            <PatientPermissionsIcon
                                size='24'
                                color={newGroup ? 'midGrey' : 'grey'}
                                noCircularContainer
                            />
                        }
                        exact
                        disabled={newGroup}
                        title={'Permissions'}
                        pathname={this.props.location.pathname}
                        location={getGroupPermissionsRoute(this.props.match.params.id)}
                        referrerLocation={this.props.location}
                    />
                </NavMenu>
            </>
        );
    };

    public render() {
        const newGroup = this.props.match.params.id === 'new';

        const contents = (
            <Switch>
                <Route
                    path={routeForNewGroup}
                    exact
                    render={(
                        routerProps: RouteComponentProps<any> // Not a great solution, but authorisation needs it's types overhauled
                    ) => (
                        <AsyncFetch
                            key='new-information'
                            fetch={this.groupFileStore.loadGroupInformation()}
                            render={({ data, refresh, loading }) => {
                                if (loading || !data) return <LoadingPlaceholder />;

                                return (
                                    <GroupFileInformation
                                        {...routerProps}
                                        key='new'
                                        defaultGroup={data.defaultGroup}
                                        group={data.group}
                                        isNew
                                        isDeleting={this.state.isDeleting}
                                        loadInformation={refresh}
                                        onError={this.groupFileStore.setFormError}
                                        onSave={this.saveGroupInformation}
                                        onClose={this.close}
                                    />
                                );
                            }}
                        />
                    )}
                />
                <Route
                    path={routeForGroup}
                    exact
                    render={routerProps => (
                        <AsyncFetch
                            key='information'
                            fetch={this.groupFileStore.loadGroupInformation(routerProps.match.params.id)}
                            render={({ data, refresh, loading }) => {
                                if (loading || !data) return <LoadingPlaceholder />;

                                return (
                                    <GroupFileInformation
                                        {...routerProps}
                                        key={routerProps.match.params.id}
                                        isDeleting={this.state.isDeleting}
                                        defaultGroup={data.defaultGroup}
                                        group={data.group}
                                        loadInformation={refresh}
                                        onError={this.groupFileStore.setFormError}
                                        onSave={this.saveGroupInformation}
                                        onClose={this.close}
                                    />
                                );
                            }}
                        />
                    )}
                />
                <Route
                    path={routeForGroupPermissions}
                    exact
                    render={({ match }) => (
                        <AsyncFetch
                            key='permissions'
                            fetch={this.groupFileStore.loadPermissions(match.params.id)}
                            render={({ data, refresh, loading }) => {
                                if (loading || !data) return <LoadingPlaceholder />;

                                return (
                                    <GroupPermissions
                                        isDeleting={this.state.isDeleting}
                                        permissions={data}
                                        onClose={this.close}
                                        loadPermissions={refresh}
                                        onError={this.groupFileStore.setFormError}
                                        onUpdatePermissions={this.groupFileStore.updatePermissions(match.params.id)}
                                    />
                                );
                            }}
                        />
                    )}
                />
            </Switch>
        );

        const alerts = this.groupFileStore.formError && (
            <AlertBanner promptMessage={formErrorMessage} promptType={AlertBannerTypes.Error} />
        );

        const actionButtons = () => {
            const closeIcon = (
                <>
                    <Tooltip id='close-icon' title='Close'>
                        <IconButton onClick={this.close}>
                            <CloseIcon color={white50} />
                        </IconButton>
                    </Tooltip>
                </>
            );
            if (this.props.match.params.id === 'new') return closeIcon;

            return (
                <>
                    <Tooltip title='Delete group'>
                        <IconButton onClick={this.onDelete}>
                            <DeleteIcon color={white50} />
                        </IconButton>
                    </Tooltip>
                    {closeIcon}
                </>
            );
        };

        const header = (
            <StyledDialogAppBar title={newGroup ? 'Create group' : 'Group details'} titleBarRight={actionButtons()} />
        );

        return (
            <StyledDialog
                open
                modal
                size='lg'
                titleComponent={header}
                contentComponent={
                    <>
                        {contents}
                        <StyledPrompt
                            open={this.state.confirmDeleteDialog}
                            title={'Delete group'}
                            onCancel={this.hideDeleteDialog}
                            actions={
                                <>
                                    <StyledDialogButton
                                        borderColour={divider}
                                        mode={ButtonMode.border}
                                        testId='cancel'
                                        disabled={this.state.isDeleting}
                                        onClick={this.hideDeleteDialog}
                                    >
                                        Cancel
                                    </StyledDialogButton>
                                    <StyledDialogButton
                                        variant={ButtonVariant.danger}
                                        testId='delete'
                                        disabled={this.state.isDeleting}
                                        onClick={this.deleteGroup}
                                    >
                                        Delete
                                    </StyledDialogButton>
                                </>
                            }
                        >
                            Are you sure you wish to remove this group?
                        </StyledPrompt>
                    </>
                }
                alertContent={alerts}
                sidebarRender={this.sideBarRenderFunction}
            />
        );
    }
}
